"use client";

import { Suspense, useEffect, useRef, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Image from "next/image";

type Hero = { src: string; blurDataURL: string };

/**
 * Client half of /manifesto. Long-scroll prospect-pitch page with a
 * read-gated [enter] CTA. Server component picks the hero photo per
 * request — bg stays fixed-position behind the scrolling content so
 * the photo doesn't scroll-away while the user reads.
 */
export default function ManifestoForm({ hero }: { hero: Hero }) {
  return (
    <Suspense fallback={null}>
      <ManifestoFormInner hero={hero} />
    </Suspense>
  );
}

function ManifestoFormInner({ hero }: { hero: Hero }) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [loading, setLoading] = useState(false);

  const [hasReadEnough, setHasReadEnough] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!endRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setHasReadEnough(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(endRef.current);
    return () => observer.disconnect();
  }, []);

  const handleEnter = async () => {
    setLoading(true);
    const res = await fetch("/api/manifesto-seen", { method: "POST" });
    if (res.ok) {
      const dest = searchParams.get("from") || "/";
      router.replace(dest);
    } else {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Fixed-position bg layer — photo stays put while content
          scrolls. -z-20 puts it under everything; the overlay sits
          at -z-10 above the image but below content. priority preloads
          the hero so first paint isn't waiting on it. No extra zoom —
          object-cover at its natural fit; the heavy gradient overlay
          carries the legibility job for the briefs/legs area.

          Negative top/bottom via env(safe-area-inset-*) extends the
          container UP into the status bar zone and DOWN into the
          home indicator zone. With viewport-fit=cover, `inset: 0`
          alone only fills the visible viewport — iOS Safari's status
          bar overlay then has nothing painted behind it and reads as
          a solid black band. Pushing the bg explicitly into the safe
          areas means the photo paints behind the dynamic island /
          home indicator, so the translucent system overlay shows
          photo content through it (the news-app effect). */}
      <div
        className="fixed -z-20 overflow-hidden"
        style={{
          top: "calc(-1 * env(safe-area-inset-top, 0px))",
          bottom: "calc(-1 * env(safe-area-inset-bottom, 0px))",
          left: 0,
          right: 0,
        }}
      >
        <Image
          src={hero.src}
          alt=""
          fill
          priority
          unoptimized
          sizes="100vw"
          placeholder="blur"
          blurDataURL={hero.blurDataURL}
          className="object-cover"
          style={{
            objectPosition: "center center",
            // Aggressive zoom + downward translate — see /age-check
            // for rationale (crops dark hair/shoulder area off the top
            // so iOS Safari's translucent status bar shows brighter
            // photo content rather than a solid black band).
            transform: "scale(1.25) translateY(4%)",
            // Warm-duotone treatment baked into source photos — see
            // scripts/bake-hero-duotone.mjs. Manifesto previously had a
            // slightly heavier filter (lower brightness/saturation)
            // because it overlays long-form prose; that extra darkening
            // is now carried by the heavier gradient overlay below
            // (rgba 0.78–0.92 vs age-check's 0.6–0.78), keeping the
            // legibility-vs-photo balance the same.
          }}
        />
      </div>
      {/* Overlay — heavier than /age-check because the manifesto is a
          long read. ~80% darken in the upper section keeps the wordmark
          + opening paragraphs sharp; mid stays a touch lighter so the
          photo bleeds through; bottom heavier again under the no-list
          + supporter CTA. RGB matches the bg token (#14110e). Negative
          safe-area offsets match the photo bg below — so the gradient
          extends into the same safe-area zones the photo does. */}
      <div
        className="fixed -z-10"
        style={{
          top: "calc(-1 * env(safe-area-inset-top, 0px))",
          bottom: "calc(-1 * env(safe-area-inset-bottom, 0px))",
          left: 0,
          right: 0,
          background:
            "linear-gradient(to bottom, rgba(20,17,14,0) 0%, rgba(20,17,14,0.72) 12%, rgba(20,17,14,0.68) 35%, rgba(20,17,14,0.7) 65%, rgba(20,17,14,0.8) 88%, rgba(20,17,14,0) 100%)",
        }}
      />

      <div className="relative min-h-[100dvh] flex flex-col items-center px-6 pt-12 pb-36">
        <div className="w-full max-w-sm space-y-10">
          <div className="flex flex-col items-center pt-4 gap-3">
            <svg
              width="40"
              height="30"
              viewBox="0 0 1200 900"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <defs>
                <linearGradient
                  id="dlist-logo-mintFill"
                  x1="215"
                  y1="150"
                  x2="820"
                  y2="740"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0%" stopColor="#7feeb6" />
                  <stop offset="55%" stopColor="#3fe89a" />
                  <stop offset="100%" stopColor="#2dd486" />
                </linearGradient>
                <linearGradient
                  id="dlist-logo-mintHighlight"
                  x1="740"
                  y1="180"
                  x2="990"
                  y2="560"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.60" />
                  <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0.05" />
                </linearGradient>
                <linearGradient
                  id="dlist-logo-edgeStroke"
                  x1="110"
                  y1="80"
                  x2="1060"
                  y2="820"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.95" />
                  <stop offset="50%" stopColor="#F2FFF9" stopOpacity="0.8" />
                  <stop offset="100%" stopColor="#DDF8EE" stopOpacity="0.7" />
                </linearGradient>
              </defs>
              <path
                d="M150 90C142 90 136 96 138 102L553 791C572 823 628 823 647 791L1062 102C1064 96 1058 90 1050 90H150Z"
                fill="url(#dlist-logo-edgeStroke)"
                fillOpacity="0.35"
                stroke="url(#dlist-logo-edgeStroke)"
                strokeWidth="6"
              />
              <path
                d="M215 160C207 160 201 167 204 173L555 738C574 769 626 769 645 738L996 173C999 167 993 160 985 160H215Z"
                fill="url(#dlist-logo-mintFill)"
              />
              <path
                d="M702 164H975C991 164 998 176 991 190L719 659C707 681 678 681 666 659L628 592L702 164Z"
                fill="url(#dlist-logo-mintHighlight)"
                opacity="0.75"
              />
            </svg>
            <h1
              className="text-5xl font-semibold text-cream"
              style={{
                letterSpacing: "-0.045em",
                filter: "url(#dlist-wordmark-refract)",
              }}
            >
              dlist
            </h1>
            {/* Pronunciation hint — sits between wordmark and page-title
                subtitle so spoken-form clarification stays close to the
                brand mark itself, not the page metadata below. */}
            <p className="font-mono text-[10px] text-cream/55 uppercase tracking-[0.3em]">
              ( d-list )
            </p>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted">
              manifesto
            </p>
          </div>

          {/* Body — bumped from cream/85 to cream/95 because the photo
              behind makes lower opacities harder to read. The mono "no"
              list bumped from cream/90 to cream too. Inter sans throughout
              per the 2026-05-02 type-system call (no Fraunces here). */}
          <div
            className="space-y-5 text-[16px] leading-relaxed text-cream/95"
            style={{ fontFamily: "var(--font-inter), system-ui, sans-serif" }}
          >
            <p>
              No boardroom of straight men should get to decide what the gay experience is allowed to look like.
            </p>

            <p>
              This kind of space was never meant to be clean, corporate, or investor-friendly. It has always been local, direct, messy, funny, sexual, anonymous, and ours.
            </p>

            <p>
              This is a small, independent, men-loving-men owned platform for people who want something that still feels like it belongs to the community.
            </p>

            <ul className="space-y-1.5 font-mono text-[12px] uppercase tracking-[0.15em] text-cream">
              <NoItem>no private-equity cruising machine</NoItem>
              <NoItem>no ads</NoItem>
              <NoItem>no fake gay branding</NoItem>
              <NoItem>no app built by outsiders</NoItem>
            </ul>

            <p className="text-cream">
              Always free to use.
            </p>

            <p className="text-cream">
              $7/month keeps this alive <span className="text-muted">(servers running)</span> and independent <span className="text-muted">(ours)</span>.
            </p>

            <p className="text-cream">
              $50/year <span className="text-muted">— annual supporters get a free dlist crop top.</span>
            </p>

            <p className="font-mono text-[12px] uppercase tracking-[0.2em] text-accent">
              become a supporter.
            </p>

            <div ref={endRef} aria-hidden="true" />
          </div>
        </div>

        {/* CTA — pinned to viewport bottom. backdrop-blur-md (bumped
            from -sm) gives the button area its own discrete frosted-
            glass plate over the hero photo so the read-first / enter
            state stays sharp regardless of what's behind. */}
        <div
          className="fixed bottom-0 left-0 right-0 z-50 px-6 pt-3 bg-bg/90 backdrop-blur-md"
          style={{
            paddingBottom: "max(1.5rem, calc(env(safe-area-inset-bottom) + 0.75rem))",
          }}
        >
          <div className="max-w-sm mx-auto">
            <button
              onClick={handleEnter}
              disabled={loading || !hasReadEnough}
              className="w-full font-mono text-xs uppercase tracking-[0.25em] py-3.5 bg-accent text-bg disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
            >
              {loading
                ? "..."
                : hasReadEnough
                ? "[ enter ]"
                : "[ read first ]"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function NoItem({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex gap-2">
      <span aria-hidden className="shrink-0">
        ·
      </span>
      <span>{children}</span>
    </li>
  );
}
